import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import { defaultMemoize } from 'reselect';
import _curry from 'lodash/curry';
import _map from 'lodash/map';
import _isEmpty from 'lodash/isEmpty';
import _flatMap from 'lodash/flatMap';
import _find from 'lodash/find';

// Constants
import { EMPTY_ARRAY, EMPTY_OBJECT } from '@tekion/tekion-base/app.constants';

// Utils
import isSubString from '@tekion/tekion-base/utils/isSubString';

// Helpers
import { getValueForOption } from '../../helpers/advancedSelect.general';

/**
 * Checks whether the value belongs to the option supplied or not.
 * Given an option object extracts the value string of that option with the help of the getOptionValue function.
 * Once the value for the option is found match it with the value we are trying to find option for.
 */
var matchesValue = _curry(function (getOptionValue, value, option) {
  var _ref = option || EMPTY_OBJECT,
    groupedOptions = _ref.options;
  if (_isEmpty(groupedOptions)) {
    var optionValue = getValueForOption(getOptionValue, option);
    return optionValue === value;
  }
  return getSelectValue(getOptionValue, groupedOptions, value);
});
var groupedOptions = function groupedOptions(option) {
  var _ref2 = option || EMPTY_OBJECT,
    options = _ref2.options;
  return _isEmpty(options) ? option : _toConsumableArray(options);
};

/**
 * Given a list of options finds that option to which the value would belong to.
 * @param {Function} getOptionValue: A function which when given an option object returns a string which would be used as a value for the option
 * @param {Array} options: A list of select option object supplied by the parent comonent.
 * @param {String} value: The value whose option needs to be found.
 */
var getSelectValue = _curry(function (getOptionValue, options, value) {
  var updatedOptions = _flatMap(options, groupedOptions);
  return _find(updatedOptions, matchesValue(getOptionValue, value));
});

/**
 * This particular function below is used to convert value strings into a list option list.
 * The list of provided options is searched for to convert these value strings to options.
 * NOTE: We have used map instead of reduce in the function below with the sole assumption that we can always assume that
 * selected values are a part of the options list. If we in future come across the case where the above assumotion fails then we will use reduce.
 * @param {Function} getOptionValue: A function which when given an option object returns a string which would be used as a value for the option
 * @param {Array} options: A list of select option object supplied by the parent comonent.
 * @param {String} value: The value whose option needs to be found.
 */
export var getSelectValues = defaultMemoize(function (getOptionValue) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : EMPTY_ARRAY;
  var values = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : EMPTY_ARRAY;
  return _map(values, getSelectValue(getOptionValue, options));
});

/**
 * @param {Function} defaultFilterOption: A function which returns true if the search text is a substring of the option label.
 * @param {Object} option: Select Option.
 * @param {String} searchString: The search text.
 */
export var defaultFilterOption = function defaultFilterOption() {
  var option = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT;
  var searchText = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
  var _option$label = option.label,
    label = _option$label === void 0 ? '' : _option$label;
  if (!label || !searchText) return true;
  return isSubString(label, searchText);
};